'use client'

import { useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { DataNameCell, DataStatusCell } from '@/components/block/admin/config/data/DataManagementTableCells'
import { BaseTable } from '@/components/ui/base-table'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDatamanagementItems } from '@/hooks/useApi'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDateTime } from '@/lib/utils'
import type { DataFile } from '@/types'

interface CompanyFilesPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folderIds: number[]
  fileIds: Array<{ id: number; folderId: number | null }>
  sourceItems: Array<Pick<DataFile, 'id' | 'name' | 'type'>>
  onSave: (
    folderIds: number[],
    fileIds: Array<{ id: number; folderId: number | null }>,
    sourceItems: Array<Pick<DataFile, 'id' | 'name' | 'type'>>,
  ) => void
}

export function CompanyFilesPickerDialog({
  open,
  onOpenChange,
  folderIds,
  fileIds,
  sourceItems,
  onSave,
}: CompanyFilesPickerDialogProps) {
  const [selectedFolderIds, setSelectedFolderIds] = useState(folderIds)
  const [selectedFileIds, setSelectedFileIds] = useState(fileIds)
  const [knownItems, setKnownItems] = useState(sourceItems)
  const [folder, setFolder] = useState<DataFile | null>(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const { data, isFetching, isError, refetch } = useDatamanagementItems(
    open
      ? {
          page,
          page_size: 10,
          folder: folder?.id ?? null,
          search: debouncedSearch.trim() || undefined,
          status: 'success',
        }
      : undefined,
  )

  function toggleItem(item: DataFile) {
    if (isFetching || search !== debouncedSearch || isError) return

    setKnownItems((current) => [
      ...current.filter((known) => known.id !== item.id || known.type !== item.type),
      { id: item.id, name: item.name, type: item.type },
    ])

    if (item.type === 'folder') {
      setSelectedFolderIds((current) =>
        current.includes(item.id) ? current.filter((id) => id !== item.id) : [...current, item.id],
      )
    } else {
      if (folder && selectedFolderIds.includes(folder.id)) return

      setSelectedFileIds((current) =>
        current.some(({ id }) => id === item.id)
          ? current.filter(({ id }) => id !== item.id)
          : [...current, { id: item.id, folderId: folder?.id ?? null }],
      )
    }
  }

  const isLoading = isFetching || search !== debouncedSearch
  const isFolderSelected = folder !== null && selectedFolderIds.includes(folder.id)

  const columns: ColumnDef<DataFile>[] = [
    {
      id: 'select',
      header: () => <span className="sr-only">Select</span>,
      size: 48,
      cell: ({ row }) => (
        <div onClick={(event) => event.stopPropagation()}>
          <Checkbox
            aria-label={`Select ${row.original.name}`}
            checked={
              row.original.type === 'folder'
                ? selectedFolderIds.includes(row.original.id)
                : isFolderSelected || selectedFileIds.some(({ id }) => id === row.original.id)
            }
            disabled={isLoading || (row.original.type === 'file' && isFolderSelected)}
            onCheckedChange={() => toggleItem(row.original)}
          />
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'File name',
      cell: ({ row }) => (
        <div onClick={row.original.type === 'folder' ? (event) => event.stopPropagation() : undefined}>
          {row.original.type === 'folder' ? (
            <DataNameCell
              item={row.original}
              onNavigateFolder={(item) => {
                setFolder(item)
                setPage(1)
              }}
            />
          ) : (
            <span>{row.original.name}</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'created',
      header: 'Uploaded',
      cell: ({ row }) => formatDateTime(row.original.created),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (row.original.type === 'file' ? <DataStatusCell status={row.original.status} /> : '—'),
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-9/10 w-full max-w-7xl flex-col overflow-hidden [&>div]:flex [&>div]:min-h-0 [&>div]:flex-col [&>div]:overflow-hidden">
        <div className="flex min-h-0 flex-col gap-6">
          <DialogTitle className="shrink-0">Company files</DialogTitle>
          <div className="flex min-h-0 flex-col gap-6 overflow-y-auto">
            <Breadcrumb
              items={[
                {
                  label: 'All company files',
                  onClick: () => {
                    setFolder(null)
                    setPage(1)
                  },
                },
                ...(folder ? [{ label: folder.name }] : []),
              ]}
            />
            <Input
              type="search"
              aria-label="Search company files"
              placeholder="Search company files"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
            />
            <div aria-busy={isLoading}>
              {isError ? (
                <div role="alert" className="flex items-center gap-2">
                  Could not load company files.
                  <Button type="button" variant="secondary" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              ) : (
                <BaseTable
                  data={isLoading ? [] : (data?.items ?? [])}
                  columns={columns}
                  hideIndex
                  handleRowClick={toggleItem}
                  emptyMessage={isLoading ? 'Loading company files…' : 'No company files found.'}
                  pagination={{
                    page,
                    pageSize: 10,
                    totalItems: data?.total ?? 0,
                    totalPages: data?.total_pages ?? 1,
                    onPageChange: setPage,
                  }}
                />
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSelectedFolderIds([])
                setSelectedFileIds([])
              }}
            >
              <span>Clear all</span>
            </Button>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              <span>Cancel</span>
            </Button>
            <Button
              type="button"
              onClick={() => {
                const files = selectedFileIds.filter(
                  ({ folderId }) => folderId === null || !selectedFolderIds.includes(folderId),
                )
                onSave(
                  selectedFolderIds,
                  files,
                  knownItems.filter((item) =>
                    item.type === 'folder'
                      ? selectedFolderIds.includes(item.id)
                      : files.some(({ id }) => id === item.id),
                  ),
                )
                onOpenChange(false)
              }}
            >
              <span>Save</span>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
